import React, { PureComponent, Suspense } from "react";
import { OFFER_TYPES } from "../constants/offerTypes";
import { WIDGETS } from "../constants/widgets";
import { connect } from "react-redux";
import { setGoogleAmpIframeHeight, setIframeHeight } from "../lib/iframe";
import { offerData } from "../lib/offerData";
import {
  invalidatePhoneOffers,
  loadPhoneOffers
} from "../actions/portalData/phoneOfferData";
import {
  invalidatePropositions,
  loadPropositions
} from "../actions/portalData/propositionData";
import {
  invalidateSimOnlyOffers,
  loadSimOnlyOffers
} from "../actions/portalData/simOnlyOfferData";
import OfferResultsLoader from "../components/OfferResultsLoader";
import { isPortalDataReady } from "../lib/portalData";
import { updateFilter } from "../actions/filter";
import { changeFilter, filterMightNeedChange } from "../lib/filterManipulation";
import { updateCollapsible } from "../actions/componentStatus/collapsibles";
import { updateSort } from "../actions/sort";
import NoResults from "../components/NoResults";
import { updateOfferResultsPagination } from "../actions/componentStatus/offerResultsPagination";
import { offerTypeToOffersName } from "../lib/offerType";
import { SORTS } from "../constants/sorts";
import { onePhoneSelected } from "../lib/mainEntitySelection";

function offerResults(id, offerType) {
  switch (id) {
    case WIDGETS.inDepth_default_combi.id:
      if (offerType === OFFER_TYPES.phoneOffer) {
        return InDepthDefaultPhoneOfferOfferResults;
      } else {
        return InDepthDefaultSharedOfferResults;
      }
    case WIDGETS.inDepth_default_phoneOffer.id:
      return InDepthDefaultPhoneOfferOfferResults;
    case WIDGETS.inDepth_default_proposition.id:
      return InDepthDefaultSharedOfferResults;
    case WIDGETS.inDepth_default_simOnly.id:
      return InDepthDefaultSharedOfferResults;
    case WIDGETS.topList_default_proposition.id:
      return TopListDefaultSharedOfferResults;
    case WIDGETS.topList_default_simOnly.id:
      return TopListDefaultSharedOfferResults;
    case WIDGETS.topList_inArticle_proposition.id:
      return TopListInArticleSharedOfferResults;
    case WIDGETS.topList_inArticle_simOnly.id:
      return TopListInArticleSharedOfferResults;
    default:
      return null;
  }
}

const InDepthDefaultPhoneOfferOfferResults = React.lazy(() =>
  import("../components/widgets/inDepth/default/phoneOffer/OfferResults")
);
const InDepthDefaultSharedOfferResults = React.lazy(() =>
  import("../components/widgets/inDepth/default/shared/OfferResults")
);
const TopListDefaultSharedOfferResults = React.lazy(() =>
  import("../components/widgets/topList/default/shared/OfferResults")
);
const TopListInArticleSharedOfferResults = React.lazy(() =>
  import("../components/widgets/topList/default/shared/OfferResults")
);

const collapsibleId = "offerResults";

class OfferResultsContainer extends PureComponent {
  componentDidMount() {
    const {
      clientConfig,
      filter,
      invalidatePhoneOffers,
      invalidatePropositions,
      invalidateSimOnlyOffers,
      loadPhoneOffers,
      loadPropositions,
      loadSimOnlyOffers,
      offerType,
      portalData,
      sort,
      updateFilter,
      updateSort
    } = this.props;
    let readyForOffers = true;

    invalidatePhoneOffers();
    invalidatePropositions();
    invalidateSimOnlyOffers();

    if (offerType !== OFFER_TYPES.simOnly) {
      const mightNeedToChangeFilter = filterMightNeedChange(
        portalData,
        clientConfig,
        offerType,
        filter
      );
      const onePhone = onePhoneSelected(portalData, filter);

      if (mightNeedToChangeFilter) {
        readyForOffers = false;
        changeFilter(
          portalData,
          clientConfig,
          offerType,
          filter,
          updateFilter,
          updateSort
        );
      } else if (onePhone === true && sort !== SORTS.bestTco) {
        readyForOffers = false;
        updateSort(offerType, SORTS.bestTco);
      }
    }

    if (readyForOffers) {
      if (offerType === OFFER_TYPES.phoneOffer) {
        loadPhoneOffers();
      } else if (offerType === OFFER_TYPES.proposition) {
        loadPropositions();
      } else if (offerType === OFFER_TYPES.simOnly) {
        loadSimOnlyOffers();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      clientConfig,
      filter,
      invalidatePhoneOffers,
      invalidatePropositions,
      invalidateSimOnlyOffers,
      loadPhoneOffers,
      loadPropositions,
      loadSimOnlyOffers,
      offerType,
      portal,
      portalData,
      sort,
      updateFilter,
      updateSort
    } = this.props;
    let readyForOffers = true;

    if (
      filter !== prevProps.filter ||
      offerType !== prevProps.offerType ||
      portal.id !== prevProps.portal.id ||
      sort !== prevProps.sort
    ) {
      if (prevProps.offerType === OFFER_TYPES.phoneOffer) {
        invalidatePhoneOffers();
      } else if (prevProps.offerType === OFFER_TYPES.proposition) {
        invalidatePropositions();
      } else if (prevProps.offerType === OFFER_TYPES.simOnly) {
        invalidateSimOnlyOffers();
      }
    }
    
    if (offerType !== OFFER_TYPES.simOnly) {
      const mightNeedToChangeFilter = filterMightNeedChange(
        portalData,
        clientConfig,
        offerType,
        filter
      );

      if (mightNeedToChangeFilter) {
        readyForOffers = false;
        changeFilter(
          portalData,
          clientConfig,
          offerType,
          filter,
          updateFilter,
          updateSort
        );
      }
    }

    if (
      readyForOffers &&
      this.offerDataInvalid() &&
      (this.offerDataFetchingTime() > 5 || this.didInvalidateChanged(prevProps))
    ) {
      if (offerType === OFFER_TYPES.phoneOffer) {
        loadPhoneOffers();
      } else if (offerType === OFFER_TYPES.proposition) {
        loadPropositions();
      } else if (offerType === OFFER_TYPES.simOnly) {
        loadSimOnlyOffers();
      }
    }

    if (clientConfig.options.inGoogleAmp) {
      setGoogleAmpIframeHeight();
    } else if (clientConfig.options.inIframe) {
      setIframeHeight();
    }
  }

  render() {
    const {
      clientConfig,
      currentPage,
      offerType,
      onPageClick,
      portal,
      totalPages,
      updateCollapsed,
      updateExperiment,
      updateModal,
      widgetOptions
    } = this.props;

    try {
      const SelectedOfferResults = offerResults(clientConfig.widget, offerType);
      const offers = this.getOffers();
      const offersFetching = this.offerDataFetching();
      const offersInvalid = this.offerDataInvalid();
      const limit = clientConfig.widgetOptions[offerType].initialFilter.limit;

      if (offersFetching) {
        return <OfferResultsLoader />;
      } else if (offers && !offersInvalid) {
        return (
          <Suspense fallback={<div> </div>}>
            <SelectedOfferResults
              clientConfig={clientConfig}
              collapsed={this.collapsed()}
              currentPage={currentPage}
              onCollapseClick={updateCollapsed}
              onPageClick={onPageClick}
              offerType={offerType}
              portal={portal}
              portalOfferResults={offers}
              totalPages={totalPages}
              updateExperiment={updateExperiment}
              updateModal={updateModal}
              widgetOptions={widgetOptions}
            />
          </Suspense>
        );
      } else if (
        !offersInvalid &&
        limit > 0 &&
        this.offerDataLastUpdated() > 0
      ) {
        return <NoResults />;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  collapsed() {
    const { collapsed } = this.props;

    if (collapsed == null) {
      return true;
    } else {
      return collapsed;
    }
  }

  didInvalidateChanged(prevProps) {
    const { offerType, portalData } = this.props;

    return (
      portalData.status[`${offerTypeToOffersName(offerType)}`].didInvalidate !==
      prevProps.portalData.status[`${offerTypeToOffersName(offerType)}`]
        .didInvalidate
    );
  }

  getOffers() {
    const { clientConfig, offerType, portalData } = this.props;

    if (this.hasOfferData()) {
      return offerData(clientConfig, offerType, offerType, portalData);
    }
  }

  hasOfferData() {
    const { offerType, portalData } = this.props;

    return (
      portalData.entities[`${offerTypeToOffersName(offerType)}`].length !== 0
    );
  }

  offerDataFetching() {
    const { offerType, portalData } = this.props;

    return portalData.status[`${offerTypeToOffersName(offerType)}`].isFetching;
  }

  offerDataFetchingTime() {
    const { offerType, portalData } = this.props;

    return (
      (Date.now() -
        portalData.status[`${offerTypeToOffersName(offerType)}`]
          .startFetching) /
      1000
    );
  }

  offerDataInvalid() {
    const { offerType, portalData } = this.props;

    return portalData.status[`${offerTypeToOffersName(offerType)}`]
      .didInvalidate;
  }

  offerDataLastUpdated() {
    const { offerType, portalData } = this.props;

    return portalData.status[`${offerTypeToOffersName(offerType)}`].lastUpdated;
  }
}

function mapStateToProps(state) {
  return {
    collapsed: state.componentStatus.collapsibles[collapsibleId],
    currentPage: state.componentStatus.offerResultsPagination,
    filter: state.filter[state.offerType],
    offerType: state.offerType,
    portalData: state.portalData,
    sort: state.sort[state.offerType]
  };
}

function mapDispatchToProps(dispatch) {
  return {
    invalidatePhoneOffers: () => {
      dispatch(invalidatePhoneOffers());
    },
    invalidatePropositions: () => {
      dispatch(invalidatePropositions());
    },
    invalidateSimOnlyOffers: () => {
      dispatch(invalidateSimOnlyOffers());
    },
    loadPhoneOffers: (clientConfig, status, filter, sort, withDetails) => {
      dispatch(
        loadPhoneOffers(clientConfig, status, filter, sort, withDetails)
      );
    },
    loadPropositions: (clientConfig, status, filter, sort, withDetails) => {
      dispatch(
        loadPropositions(clientConfig, status, filter, sort, withDetails)
      );
    },
    loadSimOnlyOffers: (clientConfig, status, filter, sort, withDetails) => {
      dispatch(
        loadSimOnlyOffers(clientConfig, status, filter, sort, withDetails)
      );
    },
    updateCollapsed: collapsed => {
      dispatch(updateCollapsible(collapsibleId, collapsed));
    },
    updateFilter: (offerType, filter) => {
      dispatch(updateFilter(offerType, filter));
    },
    updateOfferResultsPagination: currentPage => {
      dispatch(updateOfferResultsPagination(currentPage));
    },
    updateSort: (offerType, sort) => {
      dispatch(updateSort(offerType, sort));
    }
  };
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  const {
    invalidatePhoneOffers,
    invalidatePropositions,
    invalidateSimOnlyOffers,
    loadPhoneOffers,
    loadPropositions,
    loadSimOnlyOffers,
    updateCollapsed,
    updateOfferResultsPagination,
    updateFilter,
    updateSort
  } = dispatchProps;
  const { collapsed, portalData, filter, offerType, sort } = stateProps;
  const { clientConfig } = ownProps;
  const withDetails = !isPortalDataReady(portalData, offerType);
  const itemsPerPage = clientConfig.widgetOptions[offerType].paginationPerPage;
  const offerResultsCount =
    portalData.entities[`${offerTypeToOffersName(offerType)}`].length;
  const totalPages = Math.ceil(offerResultsCount / itemsPerPage);

  return {
    ...stateProps,
    ...ownProps,
    invalidatePhoneOffers: () => invalidatePhoneOffers(),
    invalidatePropositions: () => invalidatePropositions(),
    invalidateSimOnlyOffers: () => invalidateSimOnlyOffers(),
    loadPhoneOffers: () =>
      loadPhoneOffers(
        clientConfig,
        portalData.status.phoneOffers,
        filter,
        sort,
        withDetails
      ),
    loadPropositions: () =>
      loadPropositions(
        clientConfig,
        portalData.status.propositions,
        filter,
        sort,
        withDetails
      ),
    loadSimOnlyOffers: () =>
      loadSimOnlyOffers(
        clientConfig,
        portalData.status.simOnlyOffers,
        filter,
        sort,
        withDetails
      ),
    onPageClick: pageNumber => updateOfferResultsPagination(pageNumber),
    totalPages: totalPages,
    updateCollapsed: () => {
      let newValue;
      if (collapsed == null) {
        newValue = false;
      } else {
        newValue = !collapsed;
      }
      updateCollapsed(newValue);
    },
    updateFilter: filter => updateFilter(offerType, filter),
    updateSort: sort => updateSort(offerType, sort)
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(OfferResultsContainer);
